import React, { useContext, useEffect, useState } from "react";
import { addDays, endOfDay, startOfDay } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "components/Graphs/Dialogue";
import { Button } from "components/Graphs/Button";
import { RadioGroup, RadioGroupItem } from "components/Graphs/RadioGroup";
import { DatePicker } from "components/Graphs/DateRangePicker";
import InputField from "components/UI-lib/InputField";
import { OrgContext } from "contexts/OrgContext";
import type { KeyRole } from "./useOrgAPIKeys";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  availableRoles: KeyRole[];
  onSubmit: (req: {
    name: string;
    roleIds: string[];
    expiresAt: string;
    orgId: string;
  }) => Promise<void>;
};

const DEFAULT_KEY_EXPIRATION_DAYS = 30;
const MAX_KEY_EXPIRATION_DAYS = 90;

const defaultKeyExpirationDate = () =>
  endOfDay(addDays(new Date(), DEFAULT_KEY_EXPIRATION_DAYS));

const maximumKeyExpirationDate = () =>
  endOfDay(addDays(new Date(), MAX_KEY_EXPIRATION_DAYS));

const CreateAPIKeyModal: React.FC<Props> = ({
  open,
  onOpenChange,
  availableRoles,
  onSubmit,
}) => {
  const orgContext = useContext(OrgContext);
  const [name, setName] = useState("");
  const [selectedRoleId, setSelectedRoleId] = useState("");
  const [expirationDate, setExpirationDate] = useState(
    defaultKeyExpirationDate,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const activeOrgId = orgContext.activeOrgId;
  const activeOrgName =
    orgContext.activeOrg?.name ||
    orgContext.orgs.find((org) => org.id === activeOrgId)?.name ||
    activeOrgId;

  useEffect(() => {
    if (open) {
      setName("");
      setSelectedRoleId("");
      setExpirationDate(defaultKeyExpirationDate());
      setError("");
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!name.trim()) {
      setError("Name is required");
      return;
    }
    if (!selectedRoleId) {
      setError("Select an access level");
      return;
    }
    if (!activeOrgId) {
      setError("No active organization selected");
      return;
    }
    if (expirationDate.getTime() <= Date.now()) {
      setError("Choose an expiration date in the future");
      return;
    }
    if (expirationDate.getTime() > maximumKeyExpirationDate().getTime()) {
      setError("Choose an expiration date within the next 90 days");
      return;
    }
    setIsSubmitting(true);
    setError("");
    try {
      await onSubmit({
        name: name.trim(),
        roleIds: [selectedRoleId],
        expiresAt: expirationDate.toISOString(),
        orgId: activeOrgId,
      });
    } catch (e: any) {
      setError(e?.message || "Failed to create API key");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Create API Key</DialogTitle>
          <DialogDescription>
            API keys allow programmatic access to the platform.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-2">
          <InputField
            label="Name"
            value={name}
            onChange={setName}
            placeholder="e.g., CI/CD pipeline"
            errorMessage=""
          />

          <div>
            <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
              Access
            </label>
            <RadioGroup
              value={selectedRoleId}
              onValueChange={setSelectedRoleId}
              className="space-y-3 max-h-48 overflow-y-auto border border-zinc-800 rounded-md p-3"
            >
              {availableRoles.map((role) => {
                const id = `create-api-key-role-${role.id}`;
                return (
                  <div key={role.id} className="flex items-start gap-2">
                    <RadioGroupItem
                      value={role.id}
                      id={id}
                      className="mt-0.5"
                    />
                    <label htmlFor={id} className="cursor-pointer">
                      <span className="block text-sm text-gray-900 dark:text-white">
                        {role.displayName}
                      </span>
                      <span className="block text-xs text-secondary">
                        {role.description}
                      </span>
                    </label>
                  </div>
                );
              })}
              {availableRoles.length === 0 && (
                <div className="text-sm text-secondary">
                  No access levels available.
                </div>
              )}
            </RadioGroup>
          </div>

          <div>
            <label
              id="create-api-key-expiration-label"
              className="block text-sm font-medium text-gray-900 dark:text-white mb-1"
            >
              Expiration date
            </label>
            <DatePicker
              aria-labelledby="create-api-key-expiration-label"
              value={expirationDate}
              onChange={(date) => {
                if (date) {
                  setExpirationDate(endOfDay(date));
                  setError("");
                }
              }}
              disabled={isSubmitting}
              fromDate={startOfDay(new Date())}
              toDate={maximumKeyExpirationDate()}
              required
              placeholder="Select expiration date"
              align="start"
              className="bg-secondaryBg"
              applyOnSelect
            />
            <p className="mt-1 text-xs text-secondary">
              Expires at the end of the selected day.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-900 dark:text-white mb-1">
              Organization
            </label>
            <div className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-borderPrimary dark:bg-secondaryBg dark:text-gray-100">
              <div>{activeOrgName || "No active organization"}</div>
              {activeOrgId && (
                <div className="mt-0.5 text-xs text-secondary">
                  {activeOrgId}
                </div>
              )}
            </div>
          </div>

          {error && <p className="text-sm text-red-500">{error}</p>}
        </div>

        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!activeOrgId}
            isLoading={isSubmitting}
            onClick={handleSubmit}
          >
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreateAPIKeyModal;
